import { Frequency, RRule } from "rrule";
import { Identifiable } from "@lifeat/shared/data-models/definition";
import { TaskItemSchedulingDetails } from "@lifeat/shared/task-management/TaskSchedulingDetails";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { CompleteTaskEvent } from "../AmplitudeTaskManagementEvents";
import { AssignTaskTagEvent, RemoveTaskTagEvent } from "../AmplitudeTaskTagEvents";
import { useTaskListContext } from "../TaskListContext";
import { selectedDetail$ } from "../TaskManagementGlobals";
import { ClientTaskItemType } from "../TaskManagementTypes";
import TaskDetails from "./TaskDetails";

type SingularTaskDetailsProps = {
    task: Identifiable<ClientTaskItemType>;
    amplitudeEventSource: AmplitudeEventSource;
};

export function SingularTaskDetails(props: SingularTaskDetailsProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const taskData = useTaskListContext();
    const isScheduled =
        props.task.document.schedulingDetails?.scheduleTime || props.task.document.schedulingDetails?.scheduleDate;

    return (
        <TaskDetails
            key={`task-details-${props.task.id}`}
            amplitudeEventSource={props.amplitudeEventSource}
            task={props.task.document}
            taskMethods={{
                buildKey: () => props.task.id,
                updateTaskStatus: (status) => taskData.updateTaskStatus(props.task.id, status),
                updateTaskTitle: (title) => taskData.updateTaskTitle(props.task.id, title),
                updateTaskDescription: (description) => taskData.updateTaskDescription(props.task.id, description),
                updateTaskTag: (tagId) => taskData.updateTaskTag(props.task.id, tagId),
                updateTaskProject: (projectId) => taskData.updateTaskProject(props.task.id, projectId),
                updateTaskDuration: (duration) => taskData.updateTaskDuration(props.task.id, duration),
                updateTaskDate: (date) => taskData.updateTaskDate(props.task.id, date),
                updateTaskTime: (time) => taskData.updateTaskTime(props.task.id, time),
                updateTaskTimeSpent: (timeSpent) => taskData.updateTaskTimeSpent(props.task.id, timeSpent),
                removeTaskDuration: () => taskData.removeTaskDuration(props.task.id),
                removeTaskTime: () => taskData.removeTaskTime(props.task.id),
                removeTaskDate: () => taskData.removeTaskDate(props.task.id),
                makeTaskRecur: isScheduled
                    ? async (frequency) => {
                          const newMaster = await callMonolith("make-task-recur", {
                              taskId: props.task.id,
                              rule: newRuleFromSingularTask(frequency, props.task.document.schedulingDetails),
                              isAllDay: props.task.document.schedulingDetails?.scheduleTime == null,
                              durationMins: props.task.document.schedulingDetails?.durationMins,
                          });
                          selectedDetail$.value = {
                              type: "recurring",
                              masterId: newMaster.id,
                              index: 0,
                          };
                      }
                    : null,
                deleteTask: () => taskData.deleteTask(props.task.id),
                removeTaskDueDate: () => taskData.removeTaskDueDate(props.task.id),
                updateTaskDueDate: (dueDate) => taskData.updateTaskDueDate(props.task.id, dueDate),
            }}
            events={{
                onComplete: () => {
                    amplitudeService.logEvent(
                        new CompleteTaskEvent({
                            type: "singular",
                            source: props.amplitudeEventSource,
                            clientId: props.task.document.clientId,
                        }),
                    );
                },
                onTagAssign: (tagId) => {
                    amplitudeService.logEvent(
                        new AssignTaskTagEvent({
                            taskType: "singular",
                            taskId: props.task.id,
                            tagId,
                            source: "task-details",
                        }),
                    );
                },
                onTagRemove: () => {
                    amplitudeService.logEvent(
                        new RemoveTaskTagEvent({ taskType: "singular", taskId: props.task.id, source: "task-details" }),
                    );
                },
            }}
        />
    );
}

/**
 * Build a new rrule string from the singular task.
 * @param frequency Frequency of the recurrence.
 * @param schedulingDetails Schedule of the singular task.
 */
const newRuleFromSingularTask = (
    frequency: Frequency,
    schedulingDetails: TaskItemSchedulingDetails | null | undefined,
): string => {
    let dtstart = new Date();
    if (schedulingDetails.scheduleDate) {
        dtstart = new Date(schedulingDetails.scheduleDate);
    } else if (schedulingDetails.scheduleTime) {
        dtstart = new Date(schedulingDetails.scheduleTime);
    }
    return new RRule({ freq: frequency, dtstart }).toString();
};
